import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CREATE_PAGE_PATH } from './publicShell'

/**
 * A ilustração da vila — lojas, escola de surf, pousada e beach café numa
 * miniatura isométrica (Figma 103:538). É a peça que faz o banner parecer um
 * anúncio e não um aviso: mostra, de uma vez, os nichos que o Explore reúne.
 *
 * Exportada em webp: o PNG do Figma tem 866 KB e este banner fica no fim de
 * toda página do diretório — 74 KB dizem a mesma coisa.
 */
const ILLUSTRATION = '/explore/banner-vila.webp'

/**
 * Gradiente do banner. Sai do azul da marca (#0462e0) e abre para um azul mais
 * claro à direita, onde a ilustração entra — o contraste do lado esquerdo é o
 * que sustenta o texto branco, e do direito a arte precisa de céu.
 *
 * Não usa o PANEL_GRADIENT da landing: aquele é bem mais escuro (#012fae) e
 * apaga a ilustração, que é clara e de baixo contraste nas bordas.
 */
const BANNER_GRADIENT =
  'linear-gradient(105deg, #0b4fd0 0%, #0462e0 45%, #2b86ea 100%)'

/**
 * O convite para criar página, no fim do diretório.
 *
 * Substitui o card que fechava CADA trilho de categoria: com uma ou duas
 * empresas por seção, o mesmo convite aparecia quatro vezes na mesma tela e
 * ocupava metade de cada fileira. Um banner só, no fim, é mais forte e não
 * compete com o conteúdo que o visitante veio ver.
 *
 * É superfície de conversão — vai receber tráfego pago —, então fala de
 * RESULTADO e não de cadastro, e promete só o que a plataforma entrega hoje:
 * página própria com fotos, serviços e WhatsApp, aparecendo no filtro e no
 * mapa, com assinatura mensal e sem comissão por cliente. Deliberadamente sem
 * número de audiência: o diretório é novo, e prometer alcance que o produto não
 * tem queima a marca no primeiro clique.
 */
export default function ExploreCtaBanner({
  className,
}: {
  className?: string
}) {
  return (
    <section
      aria-labelledby="explore-cta-titulo"
      // SEM `overflow-hidden`: é ele que deixa a ilustração passar da borda do
      // painel. O gradiente continua recortado pelos cantos arredondados —
      // `overflow` só recortaria os filhos.
      className={cn('relative rounded-[28px] sm:rounded-[40px]', className)}
      style={{ background: BANNER_GRADIENT }}
    >
      {/* Empilhado até `lg`, duas colunas a partir dali. A ilustração NÃO some no
          mobile: ela é metade do argumento do banner, e um painel só de texto
          azul lê como aviso de sistema. */}
      {/* Altura mandada pelo TEXTO, não pela imagem: antes o painel tinha 624px
          porque a arte era um quadrado de 512 mais 56 de padding dos dois lados.
          Agora o padding é menor e a ilustração ganha margem negativa — ela fica
          maior e sobra para fora, sem empurrar o painel. */}
      <div className="grid items-center gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)] lg:gap-8 lg:px-12 lg:py-9 xl:grid-cols-[minmax(0,1fr)_minmax(0,34rem)]">
        <div className="order-2 lg:order-1">
          <p className="text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-white/70 sm:text-xs">
            BeWave Explore
          </p>

          <h2
            id="explore-cta-titulo"
            className="mt-3 text-[1.625rem] font-extrabold leading-[1.12] tracking-tight text-white sm:mt-4 sm:text-[2rem]"
          >
            Seu negócio na frente de{' '}
            {/* O destaque cai na segunda metade da frase de propósito: é ela
                que carrega a promessa — não é audiência qualquer, é quem já
                está procurando o que você faz. */}
            <span className="text-[#4ade80]">quem já está procurando</span>
          </h2>

          <p className="mt-4 max-w-[34rem] text-[0.9375rem] leading-relaxed text-white/85">
            Página própria com suas fotos, seus serviços e seu WhatsApp. Você
            aparece no filtro e no mapa, e o cliente fala direto com você —{' '}
            {/* O segundo destaque é o diferencial de preço, não um adjetivo: é a
                objeção que o dono levanta ao comparar com marketplace. */}
            <span className="font-semibold text-[#4ade80]">
              sem comissão por cliente fechado.
            </span>
          </p>

          <a
            href={CREATE_PAGE_PATH}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wide text-lp-blue transition-opacity hover:opacity-90 sm:mt-7 sm:w-auto"
          >
            Criar minha página
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>

          <p className="mt-4 max-w-[26rem] text-xs leading-relaxed text-white/60">
            Assinatura a partir de R$ 8,90/mês, sem comissão por cliente
            fechado. Nossa IA monta a página em minutos — você revisa e publica.
          </p>
        </div>

        {/* Primeiro no DOM só no mobile (order): ali a arte abre o banner e
            segura o olho antes do texto; no desktop ela volta para a direita.

            Sem moldura: o webp tem fundo transparente, então a ilha flutua
            direto sobre o gradiente. A borda do desenho existia para separar um
            retângulo do fundo — aqui não há retângulo para separar, e ela só
            desenhava uma caixa em volta de nada.

            `object-contain` e não `cover`: com transparência, cortar a arte
            comeria a ponta da ilha em vez de sobrar fundo. */}
        {/* A margem negativa é o truque: ela tira da CONTA do layout o que a
            imagem tem a mais, então a arte cresce e transborda o painel sem
            aumentar a altura dele. No mobile o transbordo é menor — ali o
            banner já é estreito e sobra pouco espaço em volta. */}
        <img
          src={ILLUSTRATION}
          alt=""
          loading="lazy"
          width={900}
          height={900}
          className="order-1 -my-6 h-[280px] w-auto max-w-none justify-self-center object-contain sm:-my-8 sm:h-[340px] lg:order-2 lg:-my-16 lg:h-[480px] lg:justify-self-end"
        />
      </div>
    </section>
  )
}
